"use client";

import { LocaleSwitch, ThemeSwitch, useThemeConfig } from "nextra-theme-docs";
import type { ReactElement } from "react";

export function Footer({
  showControls = false,
}: {
  showControls?: boolean;
}): ReactElement {
  const config = useThemeConfig();

  return (
    <footer className="border-t border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex flex-col gap-8 max-sm:gap-0 md:flex-row md:justify-between max-w-360 mx-auto">
        <div className="px-6 pb-10 max-sm:pb-5 pt-5">
          {showControls && (config.i18n.length > 0 || config.darkMode) && (
            <div className="mb-3 flex">
              <div
                className="
                flex items-center gap-2
                rounded-full
                border border-neutral-200
                bg-neutral-50
                px-1 py-1
                shadow-sm
                dark:border-neutral-800
                dark:bg-neutral-900
              "
              >
                {config.i18n.length > 0 && <LocaleSwitch />}

                {config.i18n.length > 0 && config.darkMode && (
                  <div className="h-4 w-px bg-neutral-200 dark:bg-neutral-700" />
                )}

                {config.darkMode && <ThemeSwitch />}
              </div>
            </div>
          )}

          <div className="text-left">
            <a
              href="https://feather.art"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex flex-col items-start gap-1"
            >
              <span className="font-medium tracking-wide">feather.art</span>
              <span className="text-xs text-neutral-500">
                Copyright © 2025 Sketchsoft Inc. All rights reserved.
              </span>
            </a>
          </div>
        </div>

        <div className="px-6 my-auto max-sm:my-5">
          <div className="flex h-4 gap-4">
            <svg viewBox="0 0 12 12">
              <title>Youtube</title>
              <path
                d="M1.97712 1.80381C2.86343 1.66963 4.3312 1.50977 5.98689 1.50977C7.64415 1.50977 9.08595 1.66993 9.99532 1.80362C10.7799 1.91623 11.4167 2.51418 11.5669 3.30242L11.5686 3.31128C11.6761 3.92991 11.8125 4.85528 11.8125 5.99993V6.00663L11.8123 6.01332C11.7855 7.14047 11.6781 8.05849 11.5685 8.68857L11.5669 8.69746C11.4169 9.48527 10.7806 10.083 9.9966 10.1961C9.11028 10.3302 7.64254 10.4901 5.98689 10.4901C4.35614 10.4901 2.88832 10.33 1.97858 10.1963C1.19391 10.0837 0.55706 9.48573 0.40691 8.69744L0.405223 8.68859C0.295462 8.05746 0.1875 7.13484 0.1875 5.99993C0.1875 4.86502 0.295534 3.94242 0.405294 3.31129L0.406839 3.30241C0.556896 2.51461 1.19312 1.91691 1.97712 1.80381ZM4.93945 7.17993C4.93945 7.46935 5.25341 7.64967 5.5034 7.50384L7.5263 6.32382C7.77436 6.17912 7.77437 5.82069 7.5263 5.67599L5.5034 4.49596C5.25341 4.35013 4.93945 4.53046 4.93945 4.81988V7.17993Z"
                fill="currentColor"
              ></path>
            </svg>
            <svg viewBox="0 0 12 12">
              <title>Instagram</title>
              <path
                d="M0.273438 3.83594C0.273438 1.86842 1.86842 0.273438 3.83594 0.273438H8.16444C10.132 0.273438 11.7269 1.86842 11.7269 3.83594V8.16444C11.7269 10.132 10.132 11.7269 8.16444 11.7269H3.83594C1.86842 11.7269 0.273438 10.132 0.273438 8.16444V3.83594ZM3.83594 1.39844C2.48974 1.39844 1.39844 2.48974 1.39844 3.83594V8.16444C1.39844 9.51063 2.48974 10.6019 3.83594 10.6019H8.16444C9.51063 10.6019 10.6019 9.51063 10.6019 8.16444V3.83594C10.6019 2.48974 9.51063 1.39844 8.16444 1.39844H3.83594Z"
                fill="currentColor"
              ></path>
              <path
                d="M3.01694 6.0002C3.01694 4.3526 4.35259 3.01696 6.00019 3.01696C7.64779 3.01696 8.98343 4.3526 8.98343 6.0002C8.98343 7.6478 7.64779 8.98344 6.00019 8.98344C4.35259 8.98344 3.01694 7.6478 3.01694 6.0002ZM6.00019 4.14196C4.97391 4.14196 4.14194 4.97392 4.14194 6.0002C4.14194 7.02648 4.97391 7.85844 6.00019 7.85844C7.02647 7.85844 7.85843 7.02648 7.85843 6.0002C7.85843 4.97392 7.02647 4.14196 6.00019 4.14196Z"
                fill="currentColor"
              ></path>
              <path
                d="M9.52092 2.96828C9.52092 3.23628 9.30367 3.45353 9.03567 3.45353C8.76767 3.45353 8.55042 3.23628 8.55042 2.96828C8.55042 2.70029 8.76767 2.48303 9.03567 2.48303C9.30367 2.48303 9.52092 2.70029 9.52092 2.96828Z"
                fill="currentColor"
              ></path>
            </svg>
            <svg viewBox="0 0 12 12">
              <title>Discord</title>
              <path
                d="M10.1651 2.25374C9.37673 1.89655 8.54441 1.64314 7.68945 1.5C7.57246 1.70696 7.4666 1.9199 7.37232 2.13792C6.46162 2.00212 5.5355 2.00212 4.6248 2.13792C4.53048 1.91992 4.42462 1.70699 4.30767 1.5C3.45217 1.64435 2.6193 1.89836 1.8301 2.2556C0.263322 4.5495 -0.161406 6.78643 0.0509583 8.99159C0.9685 9.66245 1.99549 10.1726 3.08729 10.5C3.33313 10.1728 3.55067 9.82568 3.7376 9.46233C3.38255 9.33111 3.03988 9.16921 2.71353 8.97852C2.79942 8.91687 2.88342 8.85336 2.96459 8.79172C3.91419 9.23363 4.95062 9.46276 5.99998 9.46276C7.04934 9.46276 8.08577 9.23363 9.03537 8.79172C9.11748 8.85803 9.20148 8.92154 9.28643 8.97852C8.95945 9.16952 8.61615 9.33173 8.26047 9.46326C8.44717 9.82645 8.66472 10.1733 8.91078 10.5C10.0035 10.174 11.0313 9.664 11.949 8.99253C12.1982 6.43524 11.5233 4.21887 10.1651 2.25374ZM4.00659 7.63543C3.4148 7.63543 2.92589 7.10398 2.92589 6.45019C2.92589 5.79639 3.39781 5.26027 4.0047 5.26027C4.61159 5.26027 5.09672 5.79639 5.08634 6.45019C5.07596 7.10398 4.6097 7.63543 4.00659 7.63543ZM7.99337 7.63543C7.40064 7.63543 6.91362 7.10398 6.91362 6.45019C6.91362 5.79639 7.38554 5.26027 7.99337 5.26027C8.6012 5.26027 9.08256 5.79639 9.07218 6.45019C9.06179 7.10398 8.59648 7.63543 7.99337 7.63543Z"
                fill="currentColor"
              ></path>
            </svg>
            <svg viewBox="0 0 12 12">
              <title>X(Twitter)</title>
              <path
                d="M3.07548 1.09863C2.33163 1.09863 1.88405 1.92338 2.28944 2.54706L4.85539 6.49468L1.95217 9.97854C1.75329 10.2172 1.78553 10.5719 2.02419 10.7708C2.26284 10.9697 2.61754 10.9374 2.81642 10.6988L5.4971 7.48193L7.44231 10.4746C7.61525 10.7406 7.91103 10.9011 8.22835 10.9011H9.21396C9.95781 10.9011 10.4054 10.0764 10 9.45271L7.30716 5.30986L10.0477 2.02125C10.2465 1.78259 10.2143 1.4279 9.97565 1.22902C9.73699 1.03014 9.3823 1.06239 9.18342 1.30104L6.66544 4.32261L4.84713 1.52521C4.67419 1.25915 4.37841 1.09863 4.06109 1.09863H3.07548ZM5.91164 5.22718L3.95933 2.22363H3.42098L5.60919 5.59011L5.91164 5.22718ZM6.25091 6.57736L8.33011 9.77613H8.86846L6.55335 6.21443L6.25091 6.57736Z"
                fill="currentColor"
                clipRule="evenodd"
                fillRule="evenodd"
              ></path>
            </svg>
            <svg viewBox="0 0 12 12">
              <title>TikTok</title>
              <path
                d="M9.6815 2.40543C9.03311 1.98393 8.56485 1.3097 8.41884 0.523536C8.38731 0.353473 8.37003 0.178674 8.37003 0H6.30001L6.29655 8.26808C6.26156 9.19417 5.4974 9.93728 4.56001 9.93728C4.26886 9.93728 3.99456 9.86452 3.75309 9.73795C3.19929 9.44733 2.82002 8.86869 2.82002 8.20307C2.82002 7.24684 3.6006 6.46886 4.56001 6.46886C4.73928 6.46886 4.91078 6.49814 5.07363 6.54894V4.44274C4.90559 4.41992 4.73453 4.40571 4.56001 4.40571C2.45932 4.40571 0.75 6.10936 0.75 8.20307C0.75 9.4878 1.39407 10.6249 2.37638 11.312C2.9954 11.7451 3.7479 12 4.56001 12C6.66071 12 8.37003 10.2968 8.37003 8.20264V4.01005C9.18171 4.59084 10.1765 4.93312 11.25 4.93312V2.86998C10.672 2.86998 10.1333 2.69862 9.68193 2.40499L9.6815 2.40543Z"
                fill="currentColor"
              ></path>
            </svg>
            <svg viewBox="0 0 12 12">
              <title>Sketchfab</title>
              <path
                d="M5.9999 0.226562C2.81144 0.226562 0.227051 2.81142 0.227051 5.99988C0.227051 9.18834 2.81144 11.7732 5.9999 11.7732C9.18837 11.7732 11.7728 9.18834 11.7728 5.99988C11.7732 2.81142 9.18883 0.226562 5.9999 0.226562ZM5.49836 9.04573C5.49836 9.21893 5.31087 9.32718 5.16087 9.24059L3.12208 8.06362C3.05245 8.02343 3.00957 7.94915 3.00957 7.86876V5.4775C3.00957 5.30712 3.19159 5.19856 3.3415 5.27954L5.38029 6.38084C5.45303 6.42013 5.49836 6.49614 5.49836 6.57881V9.04573ZM6.04709 5.70665C5.98118 5.74158 5.90225 5.74158 5.83634 5.70664L3.35622 4.39189C3.19998 4.30907 3.19595 4.08668 3.34908 3.99825L5.8292 2.56604C5.89882 2.52584 5.98461 2.52583 6.05423 2.56603L8.53474 3.99825C8.68789 4.08667 8.68386 4.30908 8.5276 4.3919L6.04709 5.70665ZM8.88864 7.87431C8.88864 7.9547 8.84576 8.02897 8.77615 8.06917L6.74659 9.24101C6.59659 9.32762 6.40908 9.21936 6.40908 9.04616V6.59034C6.40908 6.50768 6.45441 6.43168 6.52713 6.39238L8.55668 5.29577C8.70659 5.21477 8.88864 5.32332 8.88864 5.49372V7.87431Z"
                fill="currentColor"
              ></path>
            </svg>
            <svg viewBox="0 0 12 12">
              <title>LinkedIn</title>
              <path
                d="M10 0.75H2C1.31 0.75 0.75 1.31 0.75 2V10C0.75 10.69 1.31 11.25 2 11.25H10C10.69 11.25 11.25 10.69 11.25 10V2C11.25 1.31 10.69 0.75 10 0.75ZM4 4.75V9.5H2.5V4.75H4ZM2.5 3.3675C2.5 3.0175 2.8 2.75 3.25 2.75C3.7 2.75 3.9825 3.0175 4 3.3675C4 3.7175 3.72 4 3.25 4C2.8 4 2.5 3.7175 2.5 3.3675ZM9.5 9.5H8C8 9.5 8 7.185 8 7C8 6.5 7.75 6 7.125 5.99H7.105C6.5 5.99 6.25 6.505 6.25 7C6.25 7.2275 6.25 9.5 6.25 9.5H4.75V4.75H6.25V5.39C6.25 5.39 6.7325 4.75 7.7025 4.75C8.695 4.75 9.5 5.4325 9.5 6.815V9.5Z"
                fill="currentColor"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </footer>
  );
}
